"use client";

import {
  AlertTriangle,
  CheckCircle2,
  Copy,
  FileSearch,
  Upload,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import {
  type BibStrings,
  useBibStrings,
} from "@/components/citation-tool/tool-strings";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { trackBibliographyImport } from "@/lib/analytics";
import { addCitations, type SavedCitation } from "@/lib/bibliography";
import {
  type ImportOutcome,
  type MetadataWarningField,
  type StagedImportEntry,
  stageBibliographyImport,
  summarizeImportOutcome,
} from "@/lib/bibliography-import";
import {
  type BibliographyImportFormat,
  parseBibliographyImport,
} from "@/lib/citation";
import { t } from "@/lib/i18n";
import type { OutputLocale } from "@/lib/output-locales";
import { formatEntries } from "@/lib/restyle";

interface BibliographyImportDialogProps {
  citations: SavedCitation[];
  lang: OutputLocale;
  onImported: () => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  style: string;
}

interface ImportReview {
  format: BibliographyImportFormat;
  overLimit: number;
  staged: StagedImportEntry[];
  total: number;
}

const FORMAT_LABELS: Record<BibliographyImportFormat, string> = {
  bibtex: "BibTeX",
  ris: "RIS",
  csljson: "CSL-JSON",
};

function warningFieldLabel(
  field: MetadataWarningField,
  bib: BibStrings
): string {
  const labels: Record<MetadataWarningField, string> = {
    accessed: bib.fieldAccessed,
    author: bib.fieldAuthor,
    container: bib.sitePublication,
    date: bib.fieldDate,
    editor: bib.fieldEditor,
    event: bib.fieldEvent,
    publisher: bib.fieldPublisher,
    title: bib.fieldTitle,
    url: bib.fieldUrl,
  };
  return labels[field];
}

function EntryWarnings({
  item,
  bib,
}: {
  item: StagedImportEntry;
  bib: BibStrings;
}) {
  if (item.warnings.length === 0) {
    return null;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {item.warnings.map((warning) => (
        <span
          className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-1 text-amber-800 text-xs dark:text-amber-300"
          key={warning}
        >
          <AlertTriangle className="size-3" />
          {t(bib.importMissingField, {
            field: warningFieldLabel(warning, bib),
          })}
        </span>
      ))}
    </div>
  );
}

function DuplicateNotice({
  item,
  bib,
}: {
  item: StagedImportEntry;
  bib: BibStrings;
}) {
  if (!item.duplicate) {
    return null;
  }
  const message =
    item.duplicate.kind === "existing"
      ? bib.importDuplicateExisting
      : bib.importDuplicateBatch;
  return (
    <p className="flex items-center gap-1.5 text-muted-foreground text-xs">
      <Copy className="size-3.5 text-amber-600" />
      {t(message, { title: item.duplicate.title ?? bib.importUntitled })}
    </p>
  );
}

function ImportReviewList({
  bib,
  review,
  setReview,
}: {
  bib: BibStrings;
  review: ImportReview;
  setReview: (review: ImportReview) => void;
}) {
  const included = review.staged.filter((item) => item.included).length;

  function setAll(nextIncluded: boolean) {
    setReview({
      ...review,
      staged: review.staged.map((item) => ({
        ...item,
        included: nextIncluded,
      })),
    });
  }

  function toggle(id: string) {
    setReview({
      ...review,
      staged: review.staged.map((item) =>
        item.id === id ? { ...item, included: !item.included } : item
      ),
    });
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-muted/40 px-3 py-2">
        <p className="font-medium text-sm">
          {t(bib.importReviewCount, {
            included,
            total: review.staged.length,
          })}
        </p>
        <div className="flex gap-2">
          <Button
            onClick={() => setAll(true)}
            size="sm"
            type="button"
            variant="ghost"
          >
            {bib.importSelectAll}
          </Button>
          <Button
            onClick={() => setAll(false)}
            size="sm"
            type="button"
            variant="ghost"
          >
            {bib.importExcludeAll}
          </Button>
        </div>
      </div>
      {review.overLimit > 0 ? (
        <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm">
          {t(bib.importLimitNotice, { count: review.overLimit })}
        </p>
      ) : null}
      <ul className="max-h-[48vh] space-y-2 overflow-y-auto pe-1">
        {review.staged.map((item, index) => (
          <li
            className="rounded-xl border border-border/70 bg-card p-3"
            key={item.id}
          >
            <label className="flex cursor-pointer items-start gap-3">
              <input
                checked={item.included}
                className="mt-1 size-4 shrink-0 accent-primary"
                onChange={() => toggle(item.id)}
                type="checkbox"
              />
              <span className="min-w-0 flex-1 space-y-2">
                <span className="block font-medium text-sm">
                  {item.entry.title?.trim() || bib.importUntitled}
                </span>
                <span className="block text-muted-foreground text-xs">
                  {[item.entry.author, item.entry.issued, item.entry.type]
                    .filter(Boolean)
                    .join(" · ") || `#${index + 1}`}
                </span>
                <DuplicateNotice bib={bib} item={item} />
                <EntryWarnings bib={bib} item={item} />
              </span>
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}

function OutcomePanel({
  bib,
  outcome,
}: {
  bib: BibStrings;
  outcome: ImportOutcome;
}) {
  return (
    <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-5">
      <div className="flex items-center gap-3">
        <CheckCircle2 className="size-6 text-emerald-600" />
        <div>
          <h3 className="font-semibold">{bib.importOutcomeTitle}</h3>
          <p className="mt-1 text-muted-foreground text-sm">
            {t(bib.importOutcomeSummary, { ...outcome })}
          </p>
        </div>
      </div>
    </div>
  );
}

export function BibliographyImportDialog({
  citations,
  lang,
  onImported,
  onOpenChange,
  open,
  style,
}: BibliographyImportDialogProps) {
  const bib = useBibStrings();
  const [text, setText] = useState("");
  const [review, setReview] = useState<ImportReview | null>(null);
  const [outcome, setOutcome] = useState<ImportOutcome | null>(null);
  const [importing, setImporting] = useState(false);

  function reset() {
    setText("");
    setReview(null);
    setOutcome(null);
  }

  function handleOpenChange(nextOpen: boolean) {
    if (importing) {
      return;
    }
    if (!nextOpen) {
      reset();
    }
    onOpenChange(nextOpen);
  }

  function handleFile(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        setText(reader.result);
      }
    };
    reader.onerror = () => toast.error(bib.importUnreadable);
    reader.readAsText(file);
    event.target.value = "";
  }

  function handleStage(event: React.FormEvent) {
    event.preventDefault();
    try {
      const parsed = parseBibliographyImport(text);
      setReview({
        format: parsed.format,
        overLimit: parsed.skipped,
        staged: stageBibliographyImport(parsed.entries, citations),
        total: parsed.total,
      });
    } catch {
      toast.error(bib.importUnparseable);
    }
  }

  async function handleCommit() {
    if (!review) {
      return;
    }
    const included = review.staged.filter((item) => item.included);
    if (included.length === 0) {
      return;
    }
    setImporting(true);
    try {
      const formatted = await formatEntries(
        included.map((item) => item.entry),
        style,
        { lang }
      );
      const valid = included.flatMap((item, index) => {
        const citation = formatted[index];
        return citation ? [{ citation, entry: item.entry, style, lang }] : [];
      });
      // This is intentionally authoritative and late: the active bibliography can
      // change in another tab while review/formatting is in progress.
      const committed = addCitations(valid, lang);
      const nextOutcome = summarizeImportOutcome(
        review.staged,
        formatted,
        committed.added,
        committed.skipped,
        review.overLimit
      );
      setOutcome(nextOutcome);
      trackBibliographyImport(review.format, committed.added);
      onImported();
    } catch {
      toast.error(bib.importFailed);
    } finally {
      setImporting(false);
    }
  }

  const includedCount =
    review?.staged.filter((item) => item.included).length ?? 0;

  return (
    <Dialog onOpenChange={handleOpenChange} open={open}>
      <DialogContent className="sm:max-w-3xl" showCloseButton={!importing}>
        <DialogHeader>
          <DialogTitle>
            {review ? bib.importReviewTitle : bib.importTitle}
          </DialogTitle>
          <DialogDescription>
            {review
              ? t(bib.importReviewIntro, {
                  format: FORMAT_LABELS[review.format],
                  total: review.total,
                })
              : `${bib.importIntro}.`}
          </DialogDescription>
        </DialogHeader>

        {outcome ? <OutcomePanel bib={bib} outcome={outcome} /> : null}

        {review && !outcome ? (
          <ImportReviewList bib={bib} review={review} setReview={setReview} />
        ) : null}

        {review || outcome ? null : (
          <form
            className="space-y-4"
            id="stage-import-form"
            onSubmit={handleStage}
          >
            <div className="space-y-2">
              <Label htmlFor="bibliography-import-file">
                {bib.importUpload}
              </Label>
              <Input
                accept=".bib,.bibtex,.ris,.json,text/plain,application/x-bibtex,application/x-research-info-systems,application/json"
                id="bibliography-import-file"
                onChange={handleFile}
                type="file"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bibliography-import-text">
                {bib.importPaste}
              </Label>
              <Textarea
                className="min-h-40 font-mono text-xs"
                id="bibliography-import-text"
                onChange={(event) => setText(event.target.value)}
                placeholder="@article{key,&#10;  title = {…},&#10;  author = {…},&#10;  year = {…}&#10;}"
                value={text}
              />
            </div>
          </form>
        )}

        <DialogFooter>
          {outcome ? (
            <Button onClick={() => handleOpenChange(false)} type="button">
              {bib.done}
            </Button>
          ) : (
            <>
              <Button
                disabled={importing}
                onClick={() => {
                  if (review) {
                    setReview(null);
                  } else {
                    handleOpenChange(false);
                  }
                }}
                type="button"
                variant="outline"
              >
                {review ? bib.importBack : bib.cancel}
              </Button>
              {review ? (
                <Button
                  disabled={importing || includedCount === 0}
                  onClick={handleCommit}
                  type="button"
                >
                  <Upload className="size-4" />
                  {importing ? bib.importing : bib.importCommit}
                </Button>
              ) : (
                <Button
                  disabled={!text.trim()}
                  form="stage-import-form"
                  type="submit"
                >
                  <FileSearch className="size-4" />
                  {bib.importReview}
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
